import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

// StaffAppliedPitches — the Applied tab of the staff AND coach requests
// pages (the two pages are twins and share this one component on purpose).
//
// Mirrors the agent page's applied surface (pages/agent/AgentTenders.js:
// fetchPitches / handleMessageClub / EmptyState) but renders CARDS instead
// of the agent's 7-column table: a self-applicant's row has no player and no
// transfer fee, and cards stack on a phone without the table's horizontal
// scroll. Mobile behaviour, declared: the status chips scroll horizontally
// in their own strip, the cards stack single-column, nothing is clipped.

// Every status the Pitch model can carry (BE dashboard/models.py
// Pitch.STATUS_CHOICES). `reject_and_ban` reads "Rejected" ON PURPOSE — the
// ban is silent by design and must never be revealed to the applicant, the
// same rule the backend enforces on its own surfaces.
const STATUS_LABELS = {
  active: "Applied",
  scouting: "Scouting",
  accepted: "Accepted",
  rejected: "Rejected",
  reject_and_ban: "Rejected",
  expired: "Expired",
  deal: "Deal",
  external_deal: "Deal",
  signed: "Signed",
};

// Chip palette — same colours the agent applied table uses for the shared
// statuses (AgentTenders.js:2638-2648).
const STATUS_STYLES = {
  active: "bg-[#F7F0E6] text-[#BE6F01]",
  scouting: "bg-[#E9F3FF] text-[#2283FF]",
  accepted: "bg-[#EBF9EE] text-[#049669]",
  deal: "bg-[#EBF9EE] text-[#049669]",
  external_deal: "bg-[#EBF9EE] text-[#049669]",
  signed: "bg-[#EBF9EE] text-[#049669]",
  rejected: "bg-red-100 text-red-600",
  reject_and_ban: "bg-red-100 text-red-600",
  expired: "bg-gray-100 text-gray-600",
};

// The filter row sends the RAW backend status value as ?status= — these are
// the four outcomes a self-applicant thinks in. The label for `active` is
// "Applied", not "Active": to the applicant the state is "I applied and the
// club has not decided yet".
const STATUS_FILTERS = [
  { id: "all", label: "All" },
  { id: "active", label: "Applied" },
  { id: "accepted", label: "Accepted" },
  { id: "rejected", label: "Rejected" },
  { id: "expired", label: "Expired" },
];

const formatPosition = (position) =>
  position
    ?.split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ") || "";

const formatDate = (dateString) => {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const AppliedEmptyState = () => (
  // Same empty pattern as the agent page (AgentTenders.js:1128-1151).
  <div className="flex flex-col items-center justify-center py-16 px-4">
    <div className="bg-gray-100 rounded-full p-4 mb-4">
      <svg
        className="w-8 h-8 text-gray-500"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
    </div>
    <h3 className="text-lg font-medium text-gray-900 mb-1 text-center">
      You haven't applied to any openings yet
    </h3>
    <p className="text-gray-500 text-center max-w-sm">
      Browse the listed requests and apply to the roles that fit you.
    </p>
  </div>
);

const AppliedPitchCard = ({ pitch, onOpenChat }) => {
  const [showMessage, setShowMessage] = useState(false);
  const status = pitch.status?.toLowerCase();
  const statusLabel = STATUS_LABELS[status] || pitch.status || "-";
  const statusStyle = STATUS_STYLES[status] || "bg-gray-100 text-gray-600";
  const position = formatPosition(pitch.tender_position);

  return (
    <div className="bg-white border border-[#D6DBEA] rounded-lg p-4 flex flex-col gap-3">
      {/* Club + position + status. The chip sits in its own shrink-proof
          corner and the text column truncates, so a long club name can never
          push the status out of the card. */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {pitch?.club?.logo ? (
            <img
              src={pitch.club.logo}
              alt={pitch.club.name}
              className="w-10 h-10 object-contain shrink-0"
            />
          ) : (
            <div className="w-10 h-10 bg-[#2283FF] rounded-full flex items-center justify-center shrink-0">
              <span className="text-white text-sm font-bold">
                {pitch?.club?.name?.charAt(0)?.toUpperCase() || "C"}
              </span>
            </div>
          )}
          <div className="min-w-0">
            {/* Wraps instead of truncating — cutting a role name off is not
                an acceptable mobile strategy; the chip keeps its own corner
                via shrink-0 regardless of how many lines this takes. */}
            <p className="text-sm font-semibold text-[#222A35] break-words">
              {position ? `Request for ${position}` : "Request"}
            </p>
            <p className="text-xs text-[#535370] break-words">
              {pitch?.club?.name || "-"}
            </p>
          </div>
        </div>
        <span
          className={`px-2 py-1 rounded text-xs font-medium whitespace-nowrap shrink-0 ${statusStyle}`}
        >
          {statusLabel}
        </span>
      </div>

      {/* Date sent + attached document */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[#535370]">
        <span>{formatDate(pitch.created_at)}</span>
        {pitch.document_url && (
          <a
            href={pitch.document_url}
            target="_blank"
            rel="noreferrer"
            className="text-[#2283FF] hover:underline truncate max-w-[200px]"
          >
            {pitch.document_filename || "Document"}
          </a>
        )}
      </div>

      {/* The applicant's own message, collapsed by default — same
          Show/Hide sentence pair the agent-request responses use. */}
      {pitch.content && (
        <div>
          <button
            onClick={() => setShowMessage((prev) => !prev)}
            className="text-xs text-[#2283FF] hover:underline"
          >
            {showMessage ? "Hide message" : "Show message"}
          </button>
          {showMessage && (
            <p className="mt-2 text-sm text-[#535370] whitespace-pre-wrap break-words">
              {pitch.content}
            </p>
          )}
        </div>
      )}

      {/* Accept opens the conversation server-side; the chat entry point
          mirrors the agent's "Message club" wiring (AgentTenders.js:1837-1851
          — channel `pitch-<id>`). Only on accepted, exactly like the agent
          table. Full-width button: on a phone the whole row is the target. */}
      {status === "accepted" && (
        <button
          onClick={() => onOpenChat(pitch)}
          className="w-full py-2 text-center border border-[#E6E6E6] bg-white text-[#1E1E1E] rounded-lg hover:bg-gray-50 text-sm"
        >
          Open chat
        </button>
      )}
    </div>
  );
};

const StaffAppliedPitches = ({
  isMobile,
  pitches,
  loading,
  error,
  pitchStatus,
  onStatusChange,
  pagination,
  onPageChange,
}) => {
  const navigate = useNavigate();

  const handleOpenChat = (pitch) => {
    navigate("/inbox", {
      state: {
        channelId: `pitch-${pitch.id}`,
        channelType: "messaging",
      },
    });
  };

  return (
    <div>
      {/* Status filter — one horizontal strip, scrollable on narrow screens
          so the chips are never squeezed or clipped. */}
      <div
        className={`flex items-center gap-2 overflow-x-auto bg-white border-b border-[#E5E6E8] py-3 ${
          isMobile ? "px-4" : "px-12"
        }`}
      >
        {STATUS_FILTERS.map((filter) => (
          <button
            key={filter.id}
            onClick={() => onStatusChange(filter.id)}
            className={`px-4 py-2 rounded-full text-sm font-normal whitespace-nowrap shrink-0 ${
              pitchStatus === filter.id
                ? "border border-[#2283FF] text-[#2283FF] bg-[#E9F3FF]"
                : "border border-[#E6E6E6] text-[#1E1E1ECC]"
            }`}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      )}

      {/* Error State */}
      {error && <div className="text-red-500 text-center py-8">{error}</div>}

      {!loading && !error && (
        <>
          {pitches.length === 0 ? (
            <AppliedEmptyState />
          ) : (
            <div
              className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4 ${
                isMobile ? "px-4" : "px-12"
              }`}
            >
              {pitches.map((pitch) => (
                <AppliedPitchCard
                  key={pitch.id}
                  pitch={pitch}
                  onOpenChat={handleOpenChat}
                />
              ))}
            </div>
          )}

          {/* Pagination — same markup as the tender grids on these pages. */}
          {pagination.count > 10 && (
            <div className="flex justify-center items-center gap-2 mt-8">
              <button
                onClick={() => onPageChange(pagination.currentPage - 1)}
                disabled={!pagination.previous}
                className="px-4 py-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              <span className="px-4 py-2">
                Page {pagination.currentPage} of{" "}
                {Math.ceil(pagination.count / 10)}
              </span>
              <button
                onClick={() => onPageChange(pagination.currentPage + 1)}
                disabled={!pagination.next}
                className="px-4 py-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default StaffAppliedPitches;
